import { useLayoutEffect, useRef } from "react"
import { motion, useInView, useReducedMotion } from "framer-motion"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import hash from "@emotion/hash"

import { useDictionary } from "contexts/Dictionary"
import { useConsole } from "contexts/Console"

import { fullGrid } from "css/grid"
import { columnWidth } from "css/columnWidth"

import useParallax from "hooks/useParallax"

import generateBackground from "utils/generateBackground"

import Image from "components/media/ImageCLD"
import ModalBox from "components/lightbox/ModalBox"
import { LightBoxProvider, useLightBox } from "components/lightbox/context"
import PopinButton from "components/popin-edito/PopinButton"
import FigCaption, { isInnerCN, innerStyle2 } from "components/figcaption/FigCaption"

const parallaxCN = `plx${hash("parallax")}`

const pseudoStyles = css`
  content: "";
  display: block;
  position: absolute;
  left: 0;
  width: 100%;
  height: 20vh;
  z-index: 0;
`

const Root = styled(motion.section)`
  ${fullGrid}
  overflow: hidden;
  position: relative;

  &.${parallaxCN} {
    picture {
      img {
        transform: translateY(var(--ypos)) scale(1.2);
      }
    }
  }

  ::before {
    ${pseudoStyles}
    z-index: 3;
    top: 0;
    background: rgb(var(--top-color));
    mask-image: linear-gradient(black, transparent);
    -webkit-mask-image: linear-gradient(black, transparent);
    pointer-events: none;
  }

  ::after {
    ${pseudoStyles}
    z-index: 3;
    bottom: 0;
    background: rgb(var(--bot-color));
    mask-image: linear-gradient(transparent, black);
    -webkit-mask-image: linear-gradient(transparent, black);
    pointer-events: none;
  }
`

const Figure = styled.figure`
  ${columnWidth}
  cursor: pointer;
  position: relative;
  display: grid;
  z-index: 2;
  --transition: 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.1s;
  clip-path: ${({ isInView, maskAnim }) => maskAnim && (isInView ? "inset(0%)" : "inset(10%)")};
  transition: clip-path var(--transition);
  will-change: clip-path;

  &.focus-visible {
    outline: 2px solid rgb(var(--focus, 0 255 255) / 1);
    outline-offset: -2px !important;
  }

  img {
    display: block;
    width: 100%;
  }

  button {
    position: absolute;
    inset-block-end: 30px;
    inset-inline-start: 50%;
    transform: translateX(-50%);
  }

  & picture,
  & img {
    grid-row: 1;
    grid-column: 1;
  }

  figcaption.${isInnerCN} {
    ${innerStyle2};
    margin-inline: 20px;
  }
`

function ImageSimpleContent({
  className,
  image_cld: image,
  width,
  background_color,
  popin_button,
  animation,
  lightbox,
  transition_gradient,
  fig_caption,
  uid,
  ...props
}) {
  const cssStyle = background_color ? generateBackground(background_color) : {}
  const maskAnim = animation === "mask"
  const parallaxAnim = animation === "parallax"

  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")
  const dictionary = useDictionary()
  const { lightBoxes, currentIndex } = useLightBox()
  const rootRef = useRef()
  const figureRef = useRef()
  const isInView = useInView(figureRef)
  const y = useParallax(rootRef)
  const console = useConsole()

  console.verbose(fig_caption, "++++++++++++")

  const lbImage = lightbox?.filter(o => Object.keys(o)[0] === "image") || []
  const lbCaption = lightbox?.filter(o => Object.keys(o)[0] === "fig_caption") || []

  let sizes = { sizes: "100vw" }
  switch (width) {
    case "l":
      sizes = { sizes: "90vw, (min-width: 48rem) 80vw" }
      break
    case "m":
      sizes = { sizes: "90vw, (min-width: 48rem) 70vw" }
      break
    case "s":
      sizes = { sizes: "90vw, (min-width: 48rem) 50vw" }
      break
  }

  useLayoutEffect(() => {
    lightBoxes.set(uid, {
      sources: lbImage.length ? lbImage[0].image.sources : image.sources,
      fig_caption: lbCaption.length && lbCaption[0]?.fig_caption,
    })
  }, [])

  const onClick = e => {
    if (e.target.tagName !== "A") {
      currentIndex.set(0)
    }
  }

  useLayoutEffect(() => {
    // add keyboard click listener
    if (!figureRef.current) return
    figureRef.current.addEventListener("keydown", onKeyDown, false)

    return () => figureRef.current.removeEventListener("keydown", onKeyDown, false)

    function onKeyDown(e) {
      const keyDown = e.key !== undefined ? e.key : e.keyCode
      if (keyDown === "Enter" || keyDown === 13 || ["Spacebar", " "].indexOf(keyDown) >= 0 || keyDown === 32) {
        e.preventDefault()
        e.target?.click()
      }
    }
  }, [])
  return (
    <Root
      ref={rootRef}
      style={{
        "--ypos": y,
        "--top-color": transition_gradient?.top_color,
        "--bot-color": transition_gradient?.bottom_color,
        ...cssStyle,
      }}
      className={`${animation === "parallax" ? parallaxCN : ""} ${className}`}
    >
      <Figure
        ref={figureRef}
        className={`width-${!width ? "xl" : width}`}
        maskAnim={maskAnim}
        parallaxAnim={parallaxAnim}
        isInView={prefersReducedMotion ? true : isInView}
        aria-label={popin_button?.reference ? null : `${image.alt} - ${dictionary.ariaLabelOpenLightbox()}`}
        onClick={onClick}
        role={popin_button?.reference ? null : "button"}
        tabIndex={"0"}
      >
        <Image {...{ ...image, ...sizes }} />
        {popin_button?.reference && <PopinButton {...popin_button} />}
        {fig_caption?.caption?.length ? <FigCaption {...fig_caption} /> : null}
      </Figure>
      <ModalBox />
    </Root>
  )
}

export default function ImageSimple(props) {
  return (
    <LightBoxProvider>
      <ImageSimpleContent {...props} />
    </LightBoxProvider>
  )
}
